<div class="page-container">
  @if (client$ | async; as clientResult) {
    @let isSupportUser = isSupportUser$ | async;
    <!-- A support user can be looking at another client's teams, so naming their own would mislabel the page. -->
    <app-toolbar
      [title]="('SLA_DASHBOARD.TITLE' | translate) + (isSupportUser === false && clientResult.data ? ' — ' + clientResult.data.name : '')"
    ></app-toolbar>

    <div class="page">
      <div class="sla-dashboard">
        @if (clientResult.isSuccess) {
          <div class="filters">
            @if (selectedMonth$ | async; as selectedMonth) {
              <mat-form-field subscriptSizing="dynamic">
                <mat-label>{{ 'SLA_DASHBOARD.MONTH' | translate }}</mat-label>
                <mat-select [ngModel]="selectedMonth" (ngModelChange)="onMonthChange($event)">
                  @for (month of monthOptions; track month.value) {
                    <mat-option [value]="month.value">{{ ('COMMON.MONTHS.' + month.nameKey | translate) + ' ' + month.year }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }

            @if (teams$ | async; as teams) {
              @if (teams.length > 1) {
                @if (selectedTeamIds$ | async; as selectedTeamIds) {
                  <app-multi-filter
                    [name]="'teamFilter'"
                    [idKey]="'id'"
                    [labelTranslationKey]="'COMMON.TEAMS'"
                    [icon]="'apartment'"
                    [fontSet]="null"
                    [values]="selectedTeamIds"
                    [options]="teams"
                    [displayWith]="displayTeam"
                    [displaySelected]="displaySelectedTeams(teams)"
                    (valueChange)="onTeamChange($event)"
                    (clear)="onTeamClear()"
                  ></app-multi-filter>
                }
              }
            }

            <button mat-flat-button color="primary" class="export-button" [disabled]="isExporting" (click)="exportToPdf()">
              <mat-icon>picture_as_pdf</mat-icon>
              {{ 'SLA_DASHBOARD.EXPORT_PDF' | translate }}
            </button>
          </div>

          @if (slaMetrics$ | async; as result) {
            @if (result.isLoading) {
              <app-spinner></app-spinner>
            } @else if (result.isError) {
              <p class="load-failure">{{ 'COMMON.ERROR_LOADING_DATA' | translate }}</p>
            } @else if (result.data; as metrics) {
              <div class="numeric-data">
                <div class="metric-card">
                  <div class="metric-label">{{ 'SLA_DASHBOARD.COMPLETED_MEETINGS' | translate }}</div>
                  <div class="metric-value">{{ metrics.volume.completedMeetingCount }}</div>
                  <div class="metric-subtext">{{ 'SLA_DASHBOARD.OF_TOTAL_MEETINGS' | translate: { total: metrics.volume.totalMeetingCount } }}</div>
                </div>
                <div class="metric-card">
                  <div class="metric-label">{{ 'SLA_DASHBOARD.TOTAL_HOURS' | translate }}</div>
                  <div class="metric-value">{{ metrics.volume.completedMeetingDurationInSeconds / 3600 | number: '1.0-0' }}</div>
                  <div class="metric-subtext">{{ 'SLA_DASHBOARD.OF_COMPLETED_MEETINGS' | translate }}</div>
                </div>
                <div class="metric-card">
                  <div class="metric-label">{{ 'SLA_DASHBOARD.ERRORED_MEETINGS' | translate }}</div>
                  <div class="metric-value">{{ metrics.volume.erroredMeetingCount }}</div>
                  <div class="metric-subtext">{{ 'SLA_DASHBOARD.OF_TOTAL_MEETINGS' | translate: { total: metrics.volume.totalMeetingCount } }}</div>
                </div>
                <div class="metric-card">
                  <div class="metric-label">{{ 'SLA_DASHBOARD.ERROR_RATE' | translate }}</div>
                  <div class="metric-value">{{ metrics.volume.erroredMeetingPercentage | number: '1.0-1' }} %</div>
                  <div class="metric-subtext">{{ 'SLA_DASHBOARD.ERRORED_OF_ALL_MEETINGS' | translate }}</div>
                </div>
              </div>

              <div class="panel">
                <div class="section-header">
                  <div>
                    <h3>{{ 'SLA_DASHBOARD.PROCESSING_TIME' | translate }}</h3>
                    <p class="section-subtitle">{{ 'SLA_DASHBOARD.PROCESSING_TIME_DESCRIPTION' | translate }}</p>
                    <p class="section-subtitle">
                      {{
                        'SLA_DASHBOARD.MEASURED_ON_MEETINGS'
                          | translate: { measured: metrics.processing.measuredMeetingCount, withAgenda: metrics.processing.meetingsWithAgendaCount }
                      }}
                    </p>
                  </div>
                </div>

                @if (metrics.processing.measuredMeetingCount === 0) {
                  <p class="no-data-message">{{ 'SLA_DASHBOARD.NO_PROCESSING_DATA' | translate }}</p>
                } @else {
                  <div class="numeric-data">
                    <div class="metric-card">
                      <div class="metric-label">{{ 'SLA_DASHBOARD.MEDIAN' | translate }}</div>
                      <div class="metric-value">{{ formatMinutes(metrics.processing.medianInSeconds) }}</div>
                    </div>
                    <div class="metric-card">
                      <div class="metric-label">{{ 'SLA_DASHBOARD.AVERAGE' | translate }}</div>
                      <div class="metric-value">{{ formatMinutes(metrics.processing.averageInSeconds) }}</div>
                    </div>
                    <div class="metric-card">
                      <div class="metric-label">{{ 'SLA_DASHBOARD.NINETIETH_PERCENTILE' | translate }}</div>
                      <div class="metric-value">{{ formatMinutes(metrics.processing.ninetiethPercentileInSeconds) }}</div>
                    </div>
                    <div class="metric-card">
                      <div class="metric-label">{{ 'SLA_DASHBOARD.LONGEST' | translate }}</div>
                      <div class="metric-value">{{ formatMinutes(metrics.processing.longestInSeconds) }}</div>
                    </div>
                  </div>

                  <div class="chart-container">
                    @if (processingDistributionChart$ | async; as chartData) {
                      <canvas baseChart [data]="chartData" [options]="processingDistributionChartOptions" [type]="'bar'"></canvas>
                    }
                  </div>
                }
              </div>

              <div class="panel">
                <div class="section-header">
                  <div>
                    <h3>{{ 'SLA_DASHBOARD.BY_TEAM' | translate }}</h3>
                  </div>
                </div>
                <app-table
                  [source]="metrics.teams"
                  [columns]="teamColumns"
                  [rowId]="teamRowId"
                  [selectable]="false"
                  [pageSize]="25"
                  [syncUrl]="true"
                  tableId="sla-by-team"
                  csvFilename="sla-by-team"
                  label="SLA_DASHBOARD.BY_TEAM"
                  rowNoun="COMMON.TEAM"
                  rowNounPlural="COMMON.TEAMS"
                />
              </div>
            }
          }
        } @else if (clientResult.isLoading) {
          <app-spinner></app-spinner>
        } @else if (clientResult.isError) {
          <h2 class="load-failure">{{ 'COMMON.FAILED_TO_LOAD' | translate }}</h2>
        }
      </div>
    </div>
  }
</div>
